/**
 * @fileoverview Asking once before a picture is gone for good.
 *
 * From the bottom on a phone, as the other small questions on these screens
 * are, and in words that say what goes with it.
 *
 * Where: EditPhotoDialog's bin.
 * API: none; the dialog deletes (usePhotoActions.remove).
 * Settings: none.
 * Words (en/es): plants.photo.* (deleteTitle, deleteBody, deleteConfirm,
 * deleteKeep); sitewide.deleting (Button loading) while it deletes.
 *
 * @module DeletePhotoConfirm
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** Whether the question is up, and what each answer does. */
interface DeletePhotoConfirmProps {
  /** Whether the panel is up. */
  isOpen: boolean;
  /** Opens or closes it (not while deleting). */
  onOpenChange: (open: boolean) => void;
  /** Deletes the photo; the panel stays up, its Delete button loading, until it is gone. */
  onConfirm: () => void;
  /** True while the delete is in flight, which also stops the panel closing. */
  isDeleting: boolean;
}

/**
 * The "Delete this photo?" panel, with Keep and Delete.
 *
 * @param props - DeletePhotoConfirmProps.
 */
export const DeletePhotoConfirm: React.FC<DeletePhotoConfirmProps> = ({
  isOpen, onOpenChange, onConfirm, isDeleting,
}) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <BottomPanel
      open={isOpen}
      onOpenChange={(open) => { if (!isDeleting) onOpenChange(open); }}
      title={t('plants.photo.deleteTitle')}
    >
      <p className="text-sm text-muted-foreground">{t('plants.photo.deleteBody')}</p>
      <ActionRow onCancel={() => onOpenChange(false)} cancelLabel={t('plants.photo.deleteKeep')} disabled={isDeleting}>
        <Button variant="destructive" size="lg" onClick={onConfirm} loading={isDeleting} busy="deleting">
          {t('plants.photo.deleteConfirm')}
        </Button>
      </ActionRow>
    </BottomPanel>
  );
};
